import { isApolloError } from "@apollo/client";
import { GraphQLErrorCodes } from "constants/global";
import { ToastContextProps, ToastTypeEnum } from "context/toastContext";
import { isError } from "lodash";
import { GraphQLErrorCode } from "types/graphql";

type ErrorNames = "graphQLErrors" | "protocolErrors" | "clientErrors" | "networkError";

type ErrorTypeParam = {
  name: ErrorNames;
  codes?: GraphQLErrorCode | GraphQLErrorCode[];
};

type GetErrorMessagesOptions = {
  errorTypes?: ErrorTypeParam | ErrorTypeParam[];
};

const defaultCodes = [GraphQLErrorCodes.INTERNAL_SERVER_ERROR];

const getErrorTypeCodes = (codes: ErrorTypeParam["codes"]): string[] => {
  if (!codes) return defaultCodes;
  if (Array.isArray(codes)) return codes;
  return [codes];
};

export const getApolloErrorMessages = (e: unknown, options?: GetErrorMessagesOptions): string[] | null => {
  if (!isError(e)) return null;

  if (!isApolloError(e)) return null;

  const errorTypesParam = options?.errorTypes || [{ name: "graphQLErrors" }];
  const errorTypes = Array.isArray(errorTypesParam) ? errorTypesParam : [errorTypesParam];

  const errorMessages: string[] = errorTypes.reduce((acc, errorType) => {
    let messages: string[] = [];

    if (errorType.name === "graphQLErrors") {
      const errors = e.graphQLErrors.filter((error) => {
        const errorTypeCodes = getErrorTypeCodes(errorType.codes);
        return errorTypeCodes.some((code) => code === error.extensions?.code);
      });

      messages = errors.map((error) => {
        if (error.extensions?.code === GraphQLErrorCodes.INTERNAL_SERVER_ERROR) {
          return error.extensions?.originalError?.error;
        }

        return error.message;
      }) as string[];
    }

    if (errorType.name === "protocolErrors") {
      messages = e.protocolErrors.map((error) => error.message);
    }

    if (errorType.name === "clientErrors") {
      messages = e.clientErrors.map((error) => error.message);
    }

    if (errorType.name === "networkError") {
      const message = e.networkError?.message;
      if (message) {
        messages = [message];
      }
    }

    return [...acc, ...messages];
  }, [] as string[]);

  return errorMessages;
};

type ParseErrorToastOptions = {
  addToast: ToastContextProps["addToast"];
  defaultText?: string;
};

export const parseApolloError = (
  e: unknown,
  toastOptions: ParseErrorToastOptions,
  options?: GetErrorMessagesOptions
): string[] | null => {
  const errorMessages = getApolloErrorMessages(e, options);

  if (errorMessages?.length) {
    errorMessages.forEach((msg) => {
      toastOptions.addToast({ text: msg, type: ToastTypeEnum.ERROR });
    });
  } else {
    toastOptions.addToast({ text: toastOptions.defaultText, type: ToastTypeEnum.ERROR });
  }

  return errorMessages;
};
